import {
  ArrowLeftOutlined,
  CalendarOutlined,
  MessageFilled,
  MessageOutlined,
} from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { Button, Input, Typography } from 'antd';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useGlobalActions } from '../../../global-context/GlobalContext';
import useIsMobile from '../../../hooks/utils/useIsMobile';
import { useStaffingService } from '../../../hooks/utils/useStaffingService';
import {
  daysOfWeek,
  getDateTimeString,
  getDateYearString,
  ISelectedDays,
} from '../../../utils/calendarUtility';
import { showToast } from '../../../utils/commonUtility';
import Loader from '../../common/loader/Loader';
import { SidebarSlider } from '../../common/sidebar-slider/SidebarSlider';
import './CalendarRemarks.scss';
import { WorkforceUnitEnum } from '../../../enums/WorkforceUnitEnum';
import { staffingStatusesData } from '../calendar-outline/CalendarOutline';

const { TextArea } = Input;
const { Text } = Typography;
export const CalendarRemarks = ({
  refetchMainCalendarData,
}: {
  refetchMainCalendarData: () => any;
}) => {
  const {
    getStatusByPages,
    remarksData,
    updateStatus,
    hasMoreData,
    deleteStatus,
    updateStatusLoading,
    deleteStatusLoading,
  } = useStaffingService();
  const { t } = useTranslate();
  const isMobile = useIsMobile();
  const [page, setPage] = useState<number>(1);

  const [availableData, setAvailableData] = useState<any>([]);
  const [openCommentDayCard, setOpenCommentDayCard] = useState<null | number>(
    null,
  );
  const fetchMoreData = () => {
    setPage(prev => prev + 1);
  };
  useEffect(() => {
    getStatusByPages(page);
  }, [page]);
  useEffect(() => {
    if (!remarksData) return;
    setAvailableData((prev: any) => [...prev, ...remarksData]);
  }, [remarksData]);
  const { setOpenedRemarksPanelType } = useGlobalActions();

  return (
    <SidebarSlider
      width={isMobile ? '100%' : '22%'}
      visible={true}
      styles={{ backgroundColor: '#fbf7f7' }}
      setVisibility={() => {
        setOpenedRemarksPanelType('NONE');
      }}
    >
      <div style={{ margin: '2rem 0', background: '#fbf7f7' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            margin: '0 1rem',
          }}
        >
          <Text
            style={{
              fontSize: '1.6rem',
              margin: '0 0.6rem 0.6rem',
              color: '#275747',
            }}
          >
            {t('remarks')}
          </Text>
          <ArrowLeftOutlined
            onClick={() => setOpenedRemarksPanelType('NONE')}
            color='#275747'
            style={{ fontWeight: '900', fontSize: '18px', cursor: 'pointer' }}
          />
        </div>
        <InfiniteScroll
          dataLength={availableData.length}
          next={fetchMoreData}
          hasMore={hasMoreData}
          scrollableTarget='sidebar-slider'
          loader={
            <div
              style={{ position: 'relative', width: '100%', height: '10rem' }}
            >
              <Loader />
            </div>
          }
        >
          {availableData.map((data: ISelectedDays, index: number) => (
            <DayCard
              key={data.id}
              setCommentOpen={id => {
                setOpenCommentDayCard(id == openCommentDayCard ? null : id);
              }}
              showComments={openCommentDayCard == data.id}
              data={data}
              deleteData={(id: number) => {
                deleteStatus(id, (data: any) => {
                  refetchMainCalendarData();
                  showToast('event-deleted', 'success');
                  setOpenedRemarksPanelType('NONE');
                });
              }}
              ctaLoading={deleteStatusLoading || updateStatusLoading}
              updateData={(
                statusid: number,
                status: string,
                reason: string,
                startDate: Date,
                endDate: Date,
                workforce?: number,
                workforceUnit?: WorkforceUnitEnum,
              ) => {
                updateStatus({
                  id: statusid,
                  status,
                  reason,
                  startDate,
                  endDate,
                  workforce,
                  workforceUnit,
                  onCompleteCb: (data: any) => {
                    showToast('event-updated', 'success');
                    refetchMainCalendarData();
                    setOpenedRemarksPanelType('NONE');
                  },
                });
              }}
            />
          ))}
        </InfiniteScroll>
      </div>
    </SidebarSlider>
  );
};

const DayCard: React.FC<{
  data: ISelectedDays;
  deleteData: (statusId: number) => void;
  showComments: boolean;
  setCommentOpen: (id: number) => any;
  updateData: (
    statusid: number,
    status: string,
    reason: string,
    startDate: Date,
    endDate: Date,
    workforce?: number,
    workforceUnit?: WorkforceUnitEnum,
  ) => void;
  ctaLoading: boolean;
}> = ({
  data,
  deleteData,
  updateData,
  ctaLoading,
  setCommentOpen,
  showComments,
}) => {
  const { t } = useTranslate();
  const [comments, setComments] = useState(data.comments);

  const toggleComments = () => {
    setCommentOpen(data.id);
  };

  const handleSubmit = () => {
    updateData(
      data.id,
      data.status,
      comments,
      data.startdate,
      data.lastdate,
      data.workforce,
      data.workforceUnit,
    );
  };

  const handleDelete = () => {
    deleteData(data.id);
  };

  return (
    <div
      className='request-card'
      style={{ backgroundColor: !showComments ? '#fbf7f7' : '#ffff' }}
    >
      <div className='request-header'>
        <div className='icon-container' style={{ background: data.color }}>
          <data.Icon className='icon' />
        </div>

        <div className='request-details'>
          <strong className='status'>
            {t(staffingStatusesData[data.status].text)}
          </strong>
          <div className='date-info'>
            <CalendarOutlined /> {t(daysOfWeek[data.startdate.getDay()])}{' '}
            {getDateYearString(data.startdate.toISOString(), t)}
          </div>
          <div className='time-info'>
            {t('updated')} {getDateTimeString(data.modifiedAt, t)}
          </div>
        </div>
      </div>
      <div className='comments-toggle' onClick={toggleComments}>
        <span className='comments-text'>{t('comments')}</span>
        {data?.comments.length ? <MessageFilled /> : <MessageOutlined />}
      </div>

      {showComments && (
        <div className='comments-section'>
          <textarea
            value={comments}
            onChange={e => setComments(e.target.value)}
            className='comments-input'
          />
          <div className='button-space'>
            <Button className='delete-button' onClick={toggleComments}>
              {t('cancel')}
            </Button>
            <Button
              className='submit-button'
              disabled={ctaLoading || data.comments == comments}
              onClick={handleSubmit}
            >
              {t('submit')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
